/**
 * Auto-tiling rules — paint a "terrain" and have the correct edge/corner tile
 * variant chosen automatically, instead of hand-picking one of many nearly
 * identical atlas tiles per cell. This is the most-requested tile-editor
 * follow-up feature (_docs/tilemap/01 §4 reserved the `tile_meta` slot for
 * it, though the rule table itself lives on the tileset — see below).
 *
 * **Scheme: 4-directional bitmask (16 patterns), user-authored rule table.**
 * Rather than assuming an external sheet-layout convention (RPG Maker's A2
 * sheets, Godot's autotile grid, and Tiled's Wang sets each use a different
 * one — hardcoding any of them would silently mismatch a real tileset that
 * doesn't follow that exact layout), the artist authors their OWN mapping:
 * for each of the 16 possible North/East/South/West same-terrain neighbor
 * patterns, they assign one or more candidate atlas tiles (multiple
 * candidates = visual variety; one is picked deterministically per cell so
 * repainting doesn't flicker). This is the same fundamental model as Tiled's
 * Terrain Brush and RPG Maker's autotiling, just without a baked-in sheet
 * layout — it works with whatever tiles a tileset actually has.
 *
 * 8-directional (47-tile "blob") autotiling, which additionally distinguishes
 * inner corners, is a documented extension — not implemented here; see
 * _docs/tilemap/07 roadmap notes. The 4-directional scheme already covers
 * the common case (edges, outer corners, center, isolated tile) correctly
 * for orthogonal tile art.
 *
 * A cell "belongs" to a terrain if the tile currently painted there is
 * tagged with that terrain name in the tileset's rule table — painting ANY
 * of a terrain's candidate tiles is what marks a cell as part of it; no
 * separate per-cell "terrain" storage is needed beyond the tile id itself.
 */

import { EMPTY_TILE, type TileID } from './constants';
import type { LayerTiles } from './chunk';
import type { AutotileTerrain } from './types';

/** North=1, East=2, South=4, West=8 — the four same-terrain neighbor bits. */
export const BIT_N = 1;
export const BIT_E = 2;
export const BIT_S = 4;
export const BIT_W = 8;

/** One terrain's rule table: bitmask pattern (0-15) → candidate atlas indices. */
export type AutotileRules = Partial<Record<number, number[]>>;

/** A cell the user just painted (now part of the terrain) or erased (no longer). */
export interface TouchedCell {
    x: number;
    y: number;
    painted: boolean;
}

export interface AutotileEdit {
    x: number;
    y: number;
    before: TileID;
    after: TileID;
}

/**
 * Deterministic candidate pick for one cell — a stable integer hash of its
 * coordinates modulo the candidate count, so the SAME cell always resolves
 * to the SAME candidate across repeated recomputes (no flicker), while
 * different cells with multiple candidates still show visual variety.
 */
export function stableCandidateIndex(cellX: number, cellY: number, candidateCount: number): number {
    if (candidateCount <= 1) return 0;
    // Classic 32-bit integer hash combine (unsigned, wraps via >>> 0).
    let h = (cellX * 374761393 + cellY * 668265263) >>> 0;
    h = (h ^ (h >>> 13)) >>> 0;
    h = Math.imul(h, 1274126177) >>> 0;
    h = (h ^ (h >>> 16)) >>> 0;
    return h % candidateCount;
}

/**
 * Build a `terrainOf` classifier from a tileset's full `autotile_rules`
 * (every terrain, not just the one being painted) — needed so a neighbor
 * belonging to a DIFFERENT terrain is correctly excluded rather than
 * silently treated as "not terrain at all" only by accident. If the same
 * atlas index somehow appears in more than one terrain's candidates (the UI
 * should prevent this), the last terrain encountered wins.
 */
export function buildTerrainLookup(autotileRules: Record<string, AutotileTerrain> | null | undefined): (tileId: TileID) => string | undefined {
    const byAtlasIndex = new Map<number, string>();
    for (const [terrainName, terrain] of Object.entries(autotileRules ?? {})) {
        for (const candidates of Object.values(terrain.rules ?? {})) {
            for (const atlasIndex of candidates) {
                byAtlasIndex.set(atlasIndex, terrainName);
            }
        }
    }
    return (tileId: TileID) => {
        if (tileId === EMPTY_TILE) return undefined;
        return byAtlasIndex.get(tileId - 1);
    };
}

/**
 * Compute the N/E/S/W bitmask at (x, y) — y-up, matching the map's own cell
 * convention (north = +y).
 */
export function computeBitmaskAt(x: number, y: number, belongsToTerrain: (x: number, y: number) => boolean): number {
    let mask = 0;
    if (belongsToTerrain(x, y + 1)) mask |= BIT_N;
    if (belongsToTerrain(x + 1, y)) mask |= BIT_E;
    if (belongsToTerrain(x, y - 1)) mask |= BIT_S;
    if (belongsToTerrain(x - 1, y)) mask |= BIT_W;
    return mask;
}

/**
 * Paint/erase a set of cells with a terrain and auto-correct their tile
 * variants (and every already-terrain neighbor's variant) to match.
 *
 * @param tiles        the layer's tile data (mutated in place via setTile,
 *                     which already marks chunks render/save-dirty)
 * @param terrainOf    classify a tile id's terrain, or undefined if it isn't
 *                     part of any autotile terrain (typically a lookup into
 *                     the tileset's tile_meta)
 * @param rules        the active terrain's bitmask → candidate-tiles table
 * @param terrainName  which terrain is being painted (only same-terrain cells
 *                     count as neighbors — a different terrain's tile is
 *                     treated as "not this terrain", giving a clean edge)
 * @param touched      the cells the user's stroke just painted or erased
 * @returns            every cell whose tile actually changed (before !== after),
 *                     in order — feed into a Stroke for undo
 */
export function applyAutotile(
    tiles: LayerTiles,
    terrainOf: (tileId: TileID) => string | undefined,
    rules: AutotileRules,
    terrainName: string,
    touched: TouchedCell[],
): AutotileEdit[] {
    const edits: AutotileEdit[] = [];
    const touchedBelongs = new Map<string, boolean>();
    for (const t of touched) {
        touchedBelongs.set(`${t.x},${t.y}`, t.painted);
        if (!t.painted) {
            const before = tiles.getTile(t.x, t.y);
            if (before !== EMPTY_TILE) {
                tiles.setTile(t.x, t.y, EMPTY_TILE);
                edits.push({ x: t.x, y: t.y, before, after: EMPTY_TILE });
            }
        }
    }

    const belongsToTerrain = (x: number, y: number): boolean => {
        const touchedState = touchedBelongs.get(`${x},${y}`);
        if (touchedState !== undefined) return touchedState;
        return terrainOf(tiles.getTile(x, y)) === terrainName;
    };

    // Recompute set: every touched cell that still belongs, plus the 4
    // neighbors of every touched cell (painted OR erased — an erased cell's
    // still-terrain neighbors need their edge redrawn too), filtered to
    // cells that actually belong to this terrain (never reassign a
    // neighbor that isn't part of it).
    const recompute = new Map<string, { x: number; y: number }>();
    const addIfBelongs = (x: number, y: number) => {
        const key = `${x},${y}`;
        if (recompute.has(key)) return;
        if (belongsToTerrain(x, y)) recompute.set(key, { x, y });
    };
    for (const t of touched) {
        addIfBelongs(t.x, t.y);
        addIfBelongs(t.x, t.y + 1);
        addIfBelongs(t.x + 1, t.y);
        addIfBelongs(t.x, t.y - 1);
        addIfBelongs(t.x - 1, t.y);
    }

    for (const { x, y } of recompute.values()) {
        const bitmask = computeBitmaskAt(x, y, belongsToTerrain);
        const candidates = rules[bitmask];
        if (!candidates || candidates.length === 0) continue; // no rule authored for this pattern — leave the tile as-is

        const atlasIndex = candidates[stableCandidateIndex(x, y, candidates.length)];
        const after = atlasIndex + 1; // TileID = atlasIndex + 1 (frozen contract, _docs/tilemap/01 §3)
        const before = tiles.getTile(x, y);
        if (before !== after) {
            tiles.setTile(x, y, after);
            edits.push({ x, y, before, after });
        }
    }

    return edits;
}
